import { Button } from '@portals/core/src/components/Button/Button';
import { ApiClientProvider } from '@portals/sip-client-data/src/general/ApiClient';
import { EstateSearchProps } from '@portals/sip-client-data/src/general/ApiClientTypes';
import { getFixedT } from 'i18next';
import { stringifyUrl } from 'query-string';
import React, { forwardRef, useCallback, useContext, useEffect, useState } from 'react';

import { AppConfig } from '../../../config';
import { PageNavigationContext } from '../../../context';
import { useEstateSearchLocalStorage } from '../../../hooks';
import { Logger } from '../../../utils';

const t = getFixedT(null, 'core-immobilien');

export interface BasicHitButtonProps extends React.ComponentProps<typeof Button> {
  values: EstateSearchProps;
  redirectUrl?: string;
}

export const BasicHitButton = forwardRef<HTMLButtonElement, BasicHitButtonProps>(
  ({ values, disabled, onClick, redirectUrl = AppConfig.estateResultsPath, ...props }, ref) => {
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const navigate = useContext(PageNavigationContext);
    const { updateLastSearchQuery } = useEstateSearchLocalStorage();

    const fetchHits = useCallback(() => {
      setLoading(true);
      ApiClientProvider.getApiClient()
        .getEstateCount(values)
        .then(({ totalItems }) => setCount(totalItems))
        .catch((e) => {
          setCount(0);
          Logger.warn(e);
        })
        .finally(() => setLoading(false));
    }, [values]);

    useEffect(() => {
      if (!disabled) {
        fetchHits();
      } else {
        setCount(0);
      }
    }, [values, disabled, fetchHits]);

    const handleRedirect = useCallback(
      (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (onClick) {
          onClick(event);
        } else {
          updateLastSearchQuery(values);
          const url = stringifyUrl({ url: redirectUrl, query: values });
          navigate(url);
        }
      },
      [navigate, onClick, redirectUrl]
    );

    return (
      <Button ref={ref} onClick={handleRedirect} loading={loading} disabled={disabled} {...props}>
        {t('searchResultFilter.showResults', { count })}
      </Button>
    );
  }
);

BasicHitButton.displayName = 'BasicHitButton';
